import { NavIcon } from '../shell/shared';
import type { VttStatus } from './useVoiceToText';

const MIC_BTN_SIZE = 32;
const RING_R = 13;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_R;

export interface MicButtonProps {
  status: VttStatus;
  /** Brand color used for idle/active outlines. */
  primary: string;
  /** Seconds elapsed since recording started. */
  elapsed: number;
  /** Max recording seconds (used to compute progress ring). */
  maxDuration: number;
  onStart: () => void;
  onStop: () => void;
  onReset: () => void;
  /** Override absolute positioning (default: top:8, right:8). */
  positionStyle?: React.CSSProperties;
}

/**
 * Circular mic button with progress ring + state-driven appearance.
 * Shared by Notes and Chief Complaint voice entry.
 */
export function MicButton({
  status,
  primary,
  elapsed,
  maxDuration,
  onStart,
  onStop,
  onReset,
  positionStyle,
}: MicButtonProps) {
  const isRecording = status === 'recording';
  const isTranscribing = status === 'transcribing';
  const isError = status === 'error';

  const progress = isRecording ? Math.min(elapsed / maxDuration, 1) : 0;
  const dashOffset = RING_CIRCUMFERENCE * (1 - progress);

  const remainingSec = Math.max(0, maxDuration - elapsed);
  const mm = String(Math.floor(remainingSec / 60));
  const ss = String(remainingSec % 60).padStart(2, '0');

  return (
    <button
      type="button"
      title={
        isRecording
          ? `Stop recording (${mm}:${ss} left)`
          : isError
            ? 'Retry'
            : isTranscribing
              ? 'Transcribing...'
              : 'Start dictation'
      }
      disabled={isTranscribing}
      onClick={() => {
        if (isRecording) onStop();
        else if (isError) onReset();
        else onStart();
      }}
      style={{
        position: 'absolute',
        top: 8,
        right: 8,
        ...positionStyle,
        width: MIC_BTN_SIZE,
        height: MIC_BTN_SIZE,
        borderRadius: '50%',
        border: 'none',
        background: isRecording ? '#dc2626' : isError ? '#fef2f2' : 'transparent',
        color: isRecording ? '#fff' : isError ? '#dc2626' : primary,
        cursor: isTranscribing ? 'wait' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        opacity: isTranscribing ? 0.4 : 1,
      }}
    >
      {isRecording && (
        <svg
          width={MIC_BTN_SIZE}
          height={MIC_BTN_SIZE}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            transform: 'rotate(-90deg)',
            pointerEvents: 'none',
          }}
        >
          <circle
            cx={MIC_BTN_SIZE / 2}
            cy={MIC_BTN_SIZE / 2}
            r={RING_R}
            fill="none"
            stroke="rgba(255,255,255,0.3)"
            strokeWidth={3}
          />
          <circle
            cx={MIC_BTN_SIZE / 2}
            cy={MIC_BTN_SIZE / 2}
            r={RING_R}
            fill="none"
            stroke="#fff"
            strokeWidth={3}
            strokeLinecap="round"
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={dashOffset}
            style={{ transition: 'stroke-dashoffset 1s linear' }}
          />
        </svg>
      )}
      <NavIcon name={isRecording ? 'mic-off' : 'mic'} />
    </button>
  );
}
